import { useMutation, useQuery, UseQueryResult } from '@tanstack/react-query';
import { localStorageUtil } from 'utils/localStorageUtil';
import { trackMixpanelEvent } from 'utils/mixpanelClient';

import {
  ESelfServeTaskStatus,
  ETaskId,
  OrderDetails,
  SelfServeTask,
  User
} from '../common/contracts';
import {
  EEventsType,
  ELocalStorageKeys,
  EOrderStatus,
  EQueryKeys
} from '../constants/enums';
import useAxios from '../hooks/useAxios';

import { EApiRoutes, fetchConfig, getApiRouteOrMock } from './api.utils';

export const useSelfServe = (currentPublisherId?: string | null) => {
  const axios = useAxios();

  const userDetails = localStorageUtil.getAny<User>(
    ELocalStorageKeys.USER_DETAILS
  );

  const getTasks: UseQueryResult<SelfServeTask[]> = useQuery({
    ...fetchConfig.general,
    queryKey: [EQueryKeys.SELF_SERVE_TASKS, currentPublisherId],
    queryFn: async () => {
      if (!userDetails?.isOnboarding) {
        return [];
      }

      if (!currentPublisherId) {
        throw new Error('No publisher ID');
      }

      const response = await axios.get(
        getApiRouteOrMock(EApiRoutes.SELF_SERVE_TASKS),
        {},
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );

      const tasks = response?.tasks as SelfServeTask[];

      try {
        const testPaymentStatus = tasks.find(
          ({ id }) => id === ETaskId.TEST_PAYMENTS
        )?.status;

        if (testPaymentStatus !== ESelfServeTaskStatus.DONE) {
          const ordersResponse = await axios.get(
            getApiRouteOrMock(EApiRoutes.ORDERS),
            {
              offset: 0,
              recordLimit: 1,
              sortValue: 'date',
              direction: 'desc'
            },
            { 'x-publishers': currentPublisherId }
          );

          const order = ordersResponse.results.find(
            (order: OrderDetails) => order.state === EOrderStatus.charge_succeed
          );

          if (order) {
            updateTask.mutate({
              id: ETaskId.TEST_PAYMENTS,
              status: ESelfServeTaskStatus.DONE
            });
            trackMixpanelEvent(EEventsType.DASHBOARD_TEST_PAYMENT_COMPLETED, {
              payment_id: order.providerPaymentId,
              amount: order.amountTotal,
              currency: order.currency,
            });
          }
        }

        return tasks;
      } catch (error) {
        console.error('Error fetching tasks', error);
        return [];
      }
    },
    enabled: !!currentPublisherId
  });

  const updateTask = useMutation({
    mutationFn: async ({
      id,
      status
    }: {
      id: string;
      status: ESelfServeTaskStatus;
    }) => {
      if (!currentPublisherId) {
        return [];
      }
      const response = await axios.put(
        `${getApiRouteOrMock(EApiRoutes.SELF_SERVE_TASKS)}/${id}`,
        { status },
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
      return response.data;
    }
  });

  const verifyBusiness = useMutation({
    mutationFn: async (data: {
      fullName: string;
      phoneNumber: string;
      email: string;
    }) => {
      const response = await axios.put(
        getApiRouteOrMock(EApiRoutes.SELF_SERVE_VERIFY_BUSINESS),
        data,
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
      return response.data;
    }
  });

  const sendAgreement = useMutation({
    mutationFn: async (data: {
      userId: string;
      companyId: string;
      publisherId: string;
    }) => {
      const response = await axios.post(
        getApiRouteOrMock(EApiRoutes.SELF_SERVE_SEND_AGREEMENT),
        data,
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
      return response.data;
    }
  });

  return {
    getTasks,
    updateTask,
    verifyBusiness,
    sendAgreement
  };
};
